import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCMS } from "../context/CMSContext";

/**
 * Reads the CMS `seo` block + `site` block and reactively updates `document.head`:
 *  - <title>
 *  - meta description / keywords / robots
 *  - Open Graph (og:title, og:description, og:image, og:type, og:url)
 *  - Twitter card meta
 *  - favicon
 *  - Google Analytics (gtag.js) when `seo.googleAnalyticsId` is set
 *
 * The static fallbacks in `public/index.html` are kept for the very first paint
 * before CMS loads; this component overrides them once CMS is available.
 *
 * Mount once near the app root (inside CMSProvider).
 */
export default function SEOHead() {
  const { cms } = useCMS();
  const gaLoadedFor = useRef(null);
  const location = useLocation();
  const pathname = location.pathname + location.search;

  // Build the desired head state from CMS
  const seo = cms?.seo || {};
  const site = cms?.site || {};

  // ---- 1) Title, meta, og, twitter, favicon ----
  useEffect(() => {
    if (!cms) return;

    if (seo.title) document.title = seo.title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content ?? "");
    };

    const setLink = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "href") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href ?? "");
    };

    // Basic
    setMeta('meta[name="description"]',  { name: "description",  content: seo.description || "" });
    setMeta('meta[name="keywords"]',     { name: "keywords",     content: seo.keywords    || "" });
    setMeta('meta[name="robots"]',       { name: "robots",       content: seo.robots || "index, follow" });

    // Open Graph
    setMeta('meta[property="og:title"]',       { property: "og:title",       content: seo.ogTitle       || seo.title || "" });
    setMeta('meta[property="og:description"]', { property: "og:description", content: seo.ogDescription || seo.description || "" });
    setMeta('meta[property="og:image"]',       { property: "og:image",       content: seo.ogImage       || "" });
    setMeta('meta[property="og:type"]',        { property: "og:type",        content: "website" });
    setMeta('meta[property="og:url"]',         { property: "og:url",         content: typeof window !== "undefined" ? window.location.href : "" });
    if (site.brandName) {
      setMeta('meta[property="og:site_name"]', { property: "og:site_name",   content: site.brandName });
    }

    // Twitter
    setMeta('meta[name="twitter:card"]',        { name: "twitter:card",        content: seo.twitterCard || "summary_large_image" });
    setMeta('meta[name="twitter:title"]',       { name: "twitter:title",       content: seo.ogTitle       || seo.title || "" });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seo.ogDescription || seo.description || "" });
    setMeta('meta[name="twitter:image"]',       { name: "twitter:image",       content: seo.ogImage       || "" });
    if (seo.twitterHandle) {
      setMeta('meta[name="twitter:site"]',    { name: "twitter:site",    content: seo.twitterHandle });
      setMeta('meta[name="twitter:creator"]', { name: "twitter:creator", content: seo.twitterHandle });
    }

    // Favicon
    if (site.faviconUrl) {
      setLink('link[rel="icon"]',          { rel: "icon",          href: site.faviconUrl });
      setLink('link[rel="apple-touch-icon"]', { rel: "apple-touch-icon", href: site.faviconUrl });
    }
  }, [cms, seo, site]);

  // ---- 2) Google Analytics (gtag.js) ----
  useEffect(() => {
    if (!cms) return;
    const gaId = (seo.googleAnalyticsId || "").trim();

    // Tear down a previously-injected GA when the ID changes or is cleared.
    const removeOld = () => {
      document.querySelectorAll('script[data-injected-ga="1"]').forEach((s) => s.remove());
      if (window.gtag) {
        try { delete window.gtag; } catch (_) { window.gtag = undefined; }
      }
      if (window.dataLayer) {
        try { delete window.dataLayer; } catch (_) { window.dataLayer = undefined; }
      }
    };

    if (!gaId) {
      if (gaLoadedFor.current) {
        removeOld();
        gaLoadedFor.current = null;
      }
      return;
    }
    if (gaLoadedFor.current === gaId) return; // already loaded for this ID

    removeOld();

    // Loader script
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    s1.setAttribute("data-injected-ga", "1");
    document.head.appendChild(s1);

    // Inline init
    const s2 = document.createElement("script");
    s2.setAttribute("data-injected-ga", "1");
    s2.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', '${gaId.replace(/'/g, "\\'")}', { anonymize_ip: true });
    `;
    document.head.appendChild(s2);

    gaLoadedFor.current = gaId;
  }, [cms, seo.googleAnalyticsId]);

  // ---- 3) Google Tag Manager ----
  const gtmLoadedFor = useRef(null);
  useEffect(() => {
    if (!cms) return;
    const id = (seo.gtmId || "").trim();

    const removeOld = () => {
      document.querySelectorAll('script[data-injected-gtm="1"]').forEach((s) => s.remove());
      document.querySelectorAll('noscript[data-injected-gtm="1"]').forEach((s) => s.remove());
    };

    if (!id) {
      if (gtmLoadedFor.current) {
        removeOld();
        gtmLoadedFor.current = null;
      }
      return;
    }
    if (gtmLoadedFor.current === id) return;
    removeOld();

    // Head script (standard GTM snippet, with our injected-gtm marker)
    const s = document.createElement("script");
    s.setAttribute("data-injected-gtm", "1");
    const safeId = id.replace(/'/g, "\\'");
    s.text = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${safeId}');
    `;
    document.head.appendChild(s);

    // Body <noscript> iframe fallback for non-JS browsers / preview bots
    const ns = document.createElement("noscript");
    ns.setAttribute("data-injected-gtm", "1");
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    if (document.body) document.body.insertBefore(ns, document.body.firstChild);

    gtmLoadedFor.current = id;
  }, [cms, seo.gtmId]);

  // ---- 4) Facebook Pixel ----
  const fbLoadedFor = useRef(null);
  useEffect(() => {
    if (!cms) return;
    const id = (seo.facebookPixelId || "").toString().trim();

    const removeOld = () => {
      document.querySelectorAll('script[data-injected-fbq="1"]').forEach((s) => s.remove());
      document.querySelectorAll('noscript[data-injected-fbq="1"]').forEach((s) => s.remove());
      if (window.fbq) {
        try { delete window.fbq; } catch (_) { window.fbq = undefined; }
      }
      if (window._fbq) {
        try { delete window._fbq; } catch (_) { window._fbq = undefined; }
      }
    };

    if (!id) {
      if (fbLoadedFor.current) {
        removeOld();
        fbLoadedFor.current = null;
      }
      return;
    }
    if (fbLoadedFor.current === id) {
      // Same id, but the route may have changed — fire a PageView so SPA
      // navigations are tracked the same as full-page loads.
      if (window.fbq) window.fbq("track", "PageView");
      return;
    }
    removeOld();

    const safeId = id.replace(/'/g, "\\'");
    const s = document.createElement("script");
    s.setAttribute("data-injected-fbq", "1");
    s.text = `
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
      document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${safeId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(s);

    // <noscript> 1x1 pixel fallback for ad-blocker / JS-off browsers
    const ns = document.createElement("noscript");
    ns.setAttribute("data-injected-fbq", "1");
    ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${encodeURIComponent(id)}&ev=PageView&noscript=1" alt="" />`;
    document.head.appendChild(ns);

    fbLoadedFor.current = id;
  }, [cms, seo.facebookPixelId, pathname]);

  // ---- 5) GA SPA page-view tracking on route change ----
  useEffect(() => {
    if (!cms) return;
    if (!gaLoadedFor.current) return;
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: pathname,
        page_location: window.location.href,
      });
    }
  }, [cms, pathname]);

  // ---- 6) Custom HTML injection into <head> and end of <body> ----
  // Admin-pasted raw HTML (scripts, meta, css, noscript, etc.). Because innerHTML
  // does NOT execute <script> tags, we parse the string with DOMParser and rebuild
  // each <script> node manually so it runs. Marked with data-injected-custom so we
  // can clean up on ID change.
  const headCodeLoaded = useRef(null);
  const bodyCodeLoaded = useRef(null);

  const injectRawHtml = (raw, target, marker) => {
    // Wipe previous
    document.querySelectorAll(`[data-injected-custom="${marker}"]`).forEach((el) => el.remove());
    if (!raw || !raw.trim()) return;
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, "text/html");
    const container = doc.body.firstChild; // our wrapping <div>
    if (!container) return;
    // Iterate a snapshot because moving children mutates childNodes
    const children = Array.from(container.childNodes);
    for (const node of children) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "SCRIPT") {
        // Recreate <script> so it actually executes
        const s = document.createElement("script");
        for (const attr of Array.from(node.attributes)) {
          s.setAttribute(attr.name, attr.value);
        }
        if (node.textContent) s.text = node.textContent;
        s.setAttribute("data-injected-custom", marker);
        target.appendChild(s);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(true);
        clone.setAttribute("data-injected-custom", marker);
        target.appendChild(clone);
      } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        // Wrap raw text in a comment-like span so the marker survives cleanup
        const span = document.createElement("span");
        span.setAttribute("data-injected-custom", marker);
        span.style.display = "none";
        span.textContent = node.textContent;
        target.appendChild(span);
      }
    }
  };

  useEffect(() => {
    if (!cms) return;
    const raw = seo.customHeaderCode || "";
    if (headCodeLoaded.current === raw) return;
    injectRawHtml(raw, document.head, "header");
    headCodeLoaded.current = raw;
  }, [cms, seo.customHeaderCode]);

  useEffect(() => {
    if (!cms) return;
    const raw = seo.customFooterCode || "";
    if (bodyCodeLoaded.current === raw) return;
    if (!document.body) return;
    injectRawHtml(raw, document.body, "footer");
    bodyCodeLoaded.current = raw;
  }, [cms, seo.customFooterCode]);

  return null;
}
